import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const ApiDeveloper = () => {
  const navigate = useNavigate();

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Helmet>
        <title>API Developer Documentation - Dexreaction</title>
        <meta name="description" content="Dexreaction API Documentation. Integrate Dexscreener reactions into your applications. Create orders, check status, and manage your account programmatically." />
        <meta name="keywords" content="dexreaction api, dexscreener api, dexmoji api, crypto api, dexscreener reactions api, automated reactions" />
        <link rel="canonical" href="https://dexreaction.com/api-developer" />
      </Helmet>

      {/* Navbar */}
      <nav className="px-6 py-4 border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <img 
            src="/logo-light.png" 
            alt="Dexreaction" 
            className="h-10 cursor-pointer" 
            onClick={() => navigate('/')}
          />
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              Home
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
            <svg className="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            <span className="text-blue-400 font-medium">REST API v2</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              API Developer
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Integrate Dexscreener reactions into your applications. Automate orders, check balances, and manage everything programmatically.
          </p>
        </div>

        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-purple-500/20 text-center">
            <p className="text-gray-400 text-sm">Endpoint</p>
            <p className="text-white font-mono font-semibold">/api/v2</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-purple-500/20 text-center">
            <p className="text-gray-400 text-sm">Method</p>
            <p className="text-white font-semibold">POST</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-purple-500/20 text-center">
            <p className="text-gray-400 text-sm">Rate Limit</p>
            <p className="text-yellow-400 font-semibold">20 req/min</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-purple-500/20 text-center">
            <p className="text-gray-400 text-sm">Payment</p>
            <p className="text-green-400 font-semibold">Balance Only</p>
          </div>
        </div>

        {/* API Base URL */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-purple-500/20 mb-8">
          <h2 className="text-xl font-semibold mb-4">API Base URL</h2>
          <div className="bg-slate-900 rounded-lg p-4 flex items-center justify-between">
            <code className="text-blue-400 font-mono">https://dexreaction.com/api/v2</code>
            <button 
              onClick={() => copyToClipboard('https://dexreaction.com/api/v2')}
              className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded hover:bg-blue-500/30 transition-colors text-sm"
            >
              Copy
            </button>
          </div>
          <p className="text-gray-400 text-sm mt-3">
            All API requests must be sent via POST with JSON or form-urlencoded body.
          </p>
        </div>

        {/* Authentication */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-purple-500/20 mb-8">
          <h2 className="text-xl font-semibold mb-4">🔐 Authentication</h2>
          <p className="text-gray-400 mb-4">
            All requests require an API key. Generate your API key from the dashboard after creating an account.
          </p>
          <div className="bg-slate-900 rounded-lg p-4">
            <pre className="text-sm text-gray-300 overflow-x-auto">{`{
  "key": "your_api_key_here",
  "action": "..."
}`}</pre>
          </div>
        </div>

        {/* Endpoints */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold mb-6">📚 API Endpoints</h2>

          {/* Get Services */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-purple-500/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-green-400">Get Services List</h3>
              <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded font-mono text-sm">action: "services"</span>
            </div>
            <p className="text-gray-400 mb-4">Retrieve all available services with pricing and limits.</p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                  Parameters
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <code className="text-blue-400">key</code>
                    <span className="text-gray-400">string <span className="text-red-400">(required)</span></span>
                  </div>
                  <div className="flex justify-between py-2">
                    <code className="text-blue-400">action</code>
                    <span className="text-gray-400">string = "services"</span>
                  </div>
                </div>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                  Example Request
                </h4>
                <pre className="text-sm text-gray-300 overflow-x-auto">{`{
  "key": "your_api_key",
  "action": "services"
}`}</pre>
              </div>
            </div>
            
            <div className="bg-slate-900/50 rounded-lg p-4 mt-4">
              <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Example Response
              </h4>
              <pre className="text-sm text-green-300 overflow-x-auto">{`[
  {"service": 1, "name": "Rocket 🚀", "type": "reaction", "rate": "0.05", "min": "500", "max": "10000"},
  {"service": 2, "name": "Fire 🔥", "type": "reaction", "rate": "0.05", "min": "500", "max": "10000"},
  {"service": 3, "name": "Poop 💩", "type": "reaction", "rate": "0.05", "min": "500", "max": "10000"},
  {"service": 4, "name": "Red Flag 🚩", "type": "reaction", "rate": "0.05", "min": "500", "max": "10000"},
  {"service": 5, "name": "Star ⭐", "type": "reaction", "rate": "0.05", "min": "500", "max": "10000"}
]`}</pre>
            </div>
          </div>

          {/* Get Balance */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-purple-500/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-green-400">Get Account Balance</h3>
              <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded font-mono text-sm">action: "balance"</span>
            </div>
            <p className="text-gray-400 mb-4">Check your current account balance in USD.</p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                  Parameters
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <code className="text-blue-400">key</code>
                    <span className="text-gray-400">string <span className="text-red-400">(required)</span></span>
                  </div>
                  <div className="flex justify-between py-2">
                    <code className="text-blue-400">action</code>
                    <span className="text-gray-400">string = "balance"</span>
                  </div>
                </div>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                  Example Request
                </h4>
                <pre className="text-sm text-gray-300 overflow-x-auto">{`{
  "key": "your_api_key",
  "action": "balance"
}`}</pre>
              </div>
            </div>
            
            <div className="bg-slate-900/50 rounded-lg p-4 mt-4">
              <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Example Response
              </h4>
              <pre className="text-sm text-green-300 overflow-x-auto">{`{"balance": "125.50000", "currency": "USD"}`}</pre>
            </div>
          </div>

          {/* Create Order */}
          <div className="bg-slate-800/50 rounded-xl p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-purple-400">Create New Order</h3>
              <span className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded font-mono text-sm">action: "add"</span>
            </div>
            <p className="text-gray-400 mb-4">Place a new order. Payment is automatically deducted from your account balance.</p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                  Parameters
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <code className="text-blue-400">key</code>
                    <span className="text-gray-400">string <span className="text-red-400">(required)</span></span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <code className="text-blue-400">action</code>
                    <span className="text-gray-400">string = "add"</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <div>
                      <code className="text-blue-400">service</code>
                      <p className="text-xs text-gray-500">Service ID (1-5)</p>
                    </div>
                    <span className="text-gray-400">integer <span className="text-red-400">(required)</span></span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <div>
                      <code className="text-blue-400">link</code>
                      <p className="text-xs text-gray-500">DexScreener pair URL</p>
                    </div>
                    <span className="text-gray-400">string <span className="text-red-400">(required)</span></span>
                  </div>
                  <div className="flex justify-between py-2">
                    <div>
                      <code className="text-blue-400">quantity</code>
                      <p className="text-xs text-gray-500">Min: 500, Max: 10000</p>
                    </div>
                    <span className="text-gray-400">integer <span className="text-red-400">(required)</span></span>
                  </div>
                </div>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                  Example Request
                </h4>
                <pre className="text-sm text-gray-300 overflow-x-auto">{`{
  "key": "your_api_key",
  "action": "add",
  "service": 1,
  "link": "https://dexscreener.com/solana/abc123",
  "quantity": 1000
}`}</pre>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  Success Response
                </h4>
                <pre className="text-sm text-green-300 overflow-x-auto">{`{"order": "550e8400-e29b-41d4-a716-446655440000"}`}</pre>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <span className="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                  Error Response
                </h4>
                <pre className="text-sm text-red-300 overflow-x-auto">{`{"error": "Insufficient balance. Required: $50.00"}`}</pre>
              </div>
            </div>
          </div>

          {/* Check Status */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-purple-500/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-green-400">Check Order Status</h3>
              <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded font-mono text-sm">action: "status"</span>
            </div>
            <p className="text-gray-400 mb-4">Get the status of one or multiple orders.</p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                  Parameters
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <code className="text-blue-400">key</code>
                    <span className="text-gray-400">string <span className="text-red-400">(required)</span></span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <code className="text-blue-400">action</code>
                    <span className="text-gray-400">string = "status"</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <div>
                      <code className="text-blue-400">order</code>
                      <p className="text-xs text-gray-500">Single order ID</p>
                    </div>
                    <span className="text-gray-400">string <span className="text-yellow-400">(optional)</span></span>
                  </div>
                  <div className="flex justify-between py-2">
                    <div>
                      <code className="text-blue-400">orders</code>
                      <p className="text-xs text-gray-500">Comma-separated IDs (max 100)</p>
                    </div>
                    <span className="text-gray-400">string <span className="text-yellow-400">(optional)</span></span>
                  </div>
                </div>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                  Example Request
                </h4>
                <pre className="text-sm text-gray-300 overflow-x-auto">{`{
  "key": "your_api_key",
  "action": "status",
  "order": "550e8400-e29b-41d4-..."
}`}</pre>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  Example Response
                </h4>
                <pre className="text-sm text-green-300 overflow-x-auto">{`{
  "charge": "50.00",
  "start_count": "0",
  "status": "Processing",
  "remains": "500",
  "currency": "USD"
}`}</pre>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                  Status Values
                </h4>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded">Pending</span>
                  <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded">Processing</span>
                  <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded">Completed</span>
                  <span className="bg-red-500/20 text-red-400 px-3 py-1 rounded">Cancelled</span>
                  <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded">Partial</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Services Table */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-purple-500/20 mt-8">
          <h2 className="text-xl font-semibold mb-4">📋 Available Services</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-600 text-left">
                  <th className="py-3 text-gray-400">ID</th>
                  <th className="py-3 text-gray-400">Name</th>
                  <th className="py-3 text-gray-400">Rate</th>
                  <th className="py-3 text-gray-400">Min</th>
                  <th className="py-3 text-gray-400">Max</th>
                  <th className="py-3 text-gray-400">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-700">
                  <td className="py-3 text-blue-400 font-mono">1</td>
                  <td className="py-3">🚀 Rocket</td>
                  <td className="py-3 text-green-400">$0.05</td>
                  <td className="py-3">500</td>
                  <td className="py-3">10,000</td>
                  <td className="py-3 text-gray-400">Bullish momentum indicator</td>
                </tr>
                <tr className="border-b border-slate-700">
                  <td className="py-3 text-blue-400 font-mono">2</td>
                  <td className="py-3">🔥 Fire</td>
                  <td className="py-3 text-green-400">$0.05</td>
                  <td className="py-3">500</td>
                  <td className="py-3">10,000</td>
                  <td className="py-3 text-gray-400">Hot & trending interest</td>
                </tr>
                <tr className="border-b border-slate-700">
                  <td className="py-3 text-blue-400 font-mono">3</td>
                  <td className="py-3">💩 Poop</td>
                  <td className="py-3 text-green-400">$0.05</td>
                  <td className="py-3">500</td>
                  <td className="py-3">10,000</td>
                  <td className="py-3 text-gray-400">Bearish sentiment</td>
                </tr>
                <tr className="border-b border-slate-700">
                  <td className="py-3 text-blue-400 font-mono">4</td>
                  <td className="py-3">🚩 Flag</td>
                  <td className="py-3 text-green-400">$0.05</td>
                  <td className="py-3">500</td>
                  <td className="py-3">10,000</td>
                  <td className="py-3 text-gray-400">Warning/caution signal</td>
                </tr>
                <tr>
                  <td className="py-3 text-blue-400 font-mono">5</td>
                  <td className="py-3">⭐ Star</td>
                  <td className="py-3 text-green-400">$0.05</td>
                  <td className="py-3">500</td>
                  <td className="py-3">10,000</td>
                  <td className="py-3 text-gray-400">Community favorite</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Error Codes */}
        <div className="bg-red-500/10 rounded-xl p-6 border border-red-500/20 mt-8">
          <h2 className="text-xl font-semibold text-red-400 mb-4">⚠️ Error Codes</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-red-500/10">
              <code className="text-red-300">"Invalid API key"</code>
              <span className="text-gray-400">API key is invalid or disabled</span>
            </div>
            <div className="flex justify-between py-2 border-b border-red-500/10">
              <code className="text-red-300">"IP not whitelisted"</code>
              <span className="text-gray-400">Your IP is not in the whitelist</span>
            </div>
            <div className="flex justify-between py-2 border-b border-red-500/10">
              <code className="text-red-300">"Rate limit exceeded"</code>
              <span className="text-gray-400">Exceeded 20 requests per minute</span>
            </div>
            <div className="flex justify-between py-2 border-b border-red-500/10">
              <code className="text-red-300">"Insufficient balance"</code>
              <span className="text-gray-400">Not enough balance for order</span>
            </div>
            <div className="flex justify-between py-2 border-b border-red-500/10">
              <code className="text-red-300">"Invalid service ID"</code>
              <span className="text-gray-400">Service ID must be 1-5</span>
            </div>
            <div className="flex justify-between py-2">
              <code className="text-red-300">"Invalid quantity"</code>
              <span className="text-gray-400">Quantity must be 500-10000, multiple of 100</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-xl p-8 border border-purple-500/20">
          <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-gray-400 mb-6">Create an account to get your API key and start integrating.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white px-8 py-3 rounded-lg font-semibold transition-all"
          >
            Create Account & Get API Key
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-purple-500/20 mt-12 py-8 text-center text-gray-400 text-sm">
        <p>&copy; 2025 Dexreaction. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default ApiDeveloper;
